import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { useCustomerStore, useCustomerStoreBase, useArchivedCustomerStore, useArchivedCustomerStoreBase, type Customer } from '../stores/customerStore';
import { useAllTagsStore } from '../stores/tagStore';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, CustomSelect, Button, BulkSelectCell, RowActions, RowAction, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton, btnPrimary, btnDisabled } from '@redpanda/ui';
import { TableSkeleton } from '@redpanda/ui';
import { useBulkActions, isAdmin, useUrlPage, useSyncUrlPage, useUrlSearch } from '@redpanda/utils';
import { createBulkDeleteOptimisticHandler, createOptimisticBulkDeleteUpdate, createBulkDeleteSuccessHandler } from '@redpanda/utils';
import CustomerDialog from '../components/CustomerDialog';
import CustomerLimitBanner from '../components/CustomerLimitBanner';
import { pendingRowClass } from '../utils/pendingItem';
import { useCustomerLimitCache } from '../hooks/useCustomerLimitCache';
import { useListSearch } from '../hooks/useListSearch';
import { bulkDeleteCustomers, getCustomers, setCustomersArchived } from '../services/api';
import { canModify, canAdminDelete } from '../utils/permissions';
import { notifyDataChanged } from '../utils/dataEvents';
import { updateCustomerArchived } from '../utils/customerStatusUpdater';
import { toast } from 'react-hot-toast';

// 標籤篩選記在 localStorage（未封存、已封存各一份）；搜尋文字放網址 ?q=（返回時保留、從側欄進來清空）
const tagsStorageKey = (archived: boolean) => `${archived ? 'archivedCustomerList' : 'customerList'}SelectedTags`;

const readStored = <T,>(key: string, fallback: T, parse: (raw: string) => T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const CustomerManagePage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const currentUserId = user?.id;
  const userRole = user?.role;

  // 由路由決定顯示未封存或已封存客戶
  const isArchived = location.pathname === '/customers/archived';
  const storeBase = isArchived ? useArchivedCustomerStoreBase : useCustomerStoreBase;

  const activeStore = useCustomerStore();
  const archivedStore = useArchivedCustomerStore();
  const currentStore = isArchived ? archivedStore : activeStore;
  const {
    customers,
    pagination,
    error,
    isPageLoading,
    showDeleteConfirmModal,
    isDeleting,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    refreshData,
  } = currentStore;

  // 標籤篩選用全部標籤
  const tags = useAllTagsStore(state => state.allTags);
  const fetchAllTags = useAllTagsStore(state => state.fetchAllTags);

  const { canCreateCustomer, setCanCreateCustomer, limitBannerRefreshTrigger, setLimitBannerRefreshTrigger } = useCustomerLimitCache();

  // 對話框（新增、編輯共用）
  const [dialogCustomer, setDialogCustomer] = useState<Customer | null | undefined>(undefined);

  // 搜尋、標籤篩選：由後端查全部客戶（原本只過濾目前這一頁）
  // 網址 ?page=、?q=：從客戶詳情返回時回到同一頁、保留搜尋
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = { pathname: location.pathname, navigationType: useNavigationType(), searchParams, setSearchParams };
  const initialPage = useUrlPage(urlPage);
  const [searchTerm, setSearchTerm] = useUrlSearch(urlPage);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(() => readStored(tagsStorageKey(isArchived), [], JSON.parse));
  const search = searchTerm.trim();
  const hasFilters = !!search || selectedTagIds.length > 0;
  const query = { archived: isArchived, search: search || undefined, tagIds: selectedTagIds };
  const searchResult = useListSearch<Customer>('customers', hasFilters ? query : null, page => getCustomers(page, query), initialPage);
  const listItems = searchResult.active ? searchResult.items : customers;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => storeBase.getState(), currentStore.fetchCustomers, currentStore.setCurrentPage);

  const bulkActions = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('customers.item', { ns: 'pages' }),
    bulkDeleteApi: bulkDeleteCustomers,
    canDeleteItem: (customer: Customer) => canAdminDelete(customer, userRole),
    userRole,
    t,
    toast,
    pagination: listPagination ? { currentPage: listPagination.currentPage, totalPages: listPagination.totalPages } : undefined,
    fetchItems: currentStore.fetchCustomers,
    setCurrentPage: currentStore.setCurrentPage,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(storeBase.setState, isArchived ? 'archivedCustomer' : 'customer', () => customers, () => pagination),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      notifyDataChanged('customers');
    },
  });

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : bulkActions.handlePageChange(page));
  useSyncUrlPage(urlPage, listPagination, changePage);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTagIds([]);
  };

  // 封存／恢復：從目前列表移除、加到另一個列表，再同步其他列表上的客戶狀態
  const moveCustomers = async (ids: string[], archived: boolean) => {
    if (ids.length === 0) {
      toast.error(archived
        ? t('customers.list.selectCustomersForArchive', { ns: 'pages' })
        : t('customers.archived.selectCustomersToRestore', { ns: 'pages' }));
      return;
    }

    const sourceBase = archived ? useCustomerStoreBase : useArchivedCustomerStoreBase;
    const targetBase = archived ? useArchivedCustomerStoreBase : useCustomerStoreBase;
    const moved = listItems.filter(customer => ids.includes(customer.id)).map(customer => ({ ...customer, archived }));
    createOptimisticBulkDeleteUpdate(sourceBase.setState, archived ? 'customer' : 'archivedCustomer')(ids, customers, pagination);
    bulkActions.setSelectedItems([]);

    try {
      const response = await setCustomersArchived(ids, archived);
      const count = response.data.updatedCount;
      toast.success(archived
        ? t('customers.list.archiveSuccess', { count, ns: 'pages' })
        : t('customers.archived.restoreSuccess', { count, ns: 'pages' }));

      targetBase.setState((state: any) => ({ items: [...moved, ...state.items], lastOptimisticUpdate: Date.now() }));
      targetBase.getState().clearCache();
      updateCustomerArchived(ids, archived);
      setLimitBannerRefreshTrigger(prev => prev + 1);
      await createBulkDeleteSuccessHandler(() => sourceBase.getState(), currentStore.fetchCustomers, currentStore.setCurrentPage)(ids);
      notifyDataChanged('customers');
    } catch (error: any) {
      console.error(archived ? '封存客戶失敗:' : '恢復客戶失敗:', error);
      const limitReached = error.response?.data?.code === 'CUSTOMER_LIMIT_REACHED';
      toast.error(limitReached ? t('customers.list.limitReached', { ns: 'pages' }) : (error.response?.data?.error || t('common.messages.operationFailed')));
      await refreshData();
    }
  };

  // 載入
  useEffect(() => {
    if (isAuthenticated && currentUserId) {
      currentStore.fetchCustomers(initialPage, false);
      fetchAllTags();
    }
  }, [isAuthenticated, currentUserId, isArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  // 切換未封存／已封存：標籤換成該模式記住的值，清除勾選（搜尋文字由 useUrlSearch 改讀新網址的 ?q=）
  useEffect(() => {
    setSelectedTagIds(readStored(tagsStorageKey(isArchived), [], JSON.parse));
    bulkActions.setSelectedItems([]);
  }, [isArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      localStorage.setItem(tagsStorageKey(isArchived), JSON.stringify(selectedTagIds));
      localStorage.removeItem(`${isArchived ? 'archivedCustomerList' : 'customerList'}SearchTerm`); // 舊版記住的搜尋文字
    } catch { /* localStorage 不可用時略過 */ }
  }, [selectedTagIds, isArchived]);

  if (isUserLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <div className="p-8 text-center text-red-500">{t('common.messages.unauthorized')}</div>;
  }

  const addButton = (
    <button
      type="button"
      onClick={() => setDialogCustomer(null)}
      disabled={!canCreateCustomer}
      className={canCreateCustomer ? btnPrimary : btnDisabled}
      title={canCreateCustomer ? undefined : t('customers.list.limitReached', { ns: 'pages' })}
    >
      {t('customers.list.addButton', { ns: 'pages' })}
    </button>
  );

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {isArchived ? t('customers.archived.title', { ns: 'pages' }) : t('customers.list.title', { ns: 'pages' })}
          </h1>
          <div className="flex space-x-3">
            {isAdmin(userRole) && (
              <MultiBulkActionButton
                selectedCount={bulkActions.selectedItems.length}
                isDeleting={bulkActions.isBulkDeleting}
                onDelete={bulkActions.handleBulkDelete}
                onArchive={isArchived ? undefined : () => moveCustomers(bulkActions.selectedItems, true)}
                onRestore={isArchived ? () => moveCustomers(bulkActions.selectedItems, false) : undefined}
              />
            )}
            {!isArchived && addButton}
            <Link to={isArchived ? '/customers' : '/customers/archived'}>
              <Button variant="secondary">
                {isArchived ? t('customers.detail.backToList', { ns: 'pages' }) : t('common.bulk.viewArchivedAction')}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <CustomerLimitBanner
        onCanCreateCustomerChange={!isArchived ? setCanCreateCustomer : undefined}
        refreshTrigger={limitBannerRefreshTrigger}
      />

      {/* 搜尋和標籤篩選（R18） */}
      <div className="mb-4 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('customers.list.searchPlaceholder', { ns: 'pages' })} />
          <div className="md:w-64">
            <CustomSelect
              value=""
              onChange={(tagId) => {
                if (tagId && !selectedTagIds.includes(tagId)) {
                  setSelectedTagIds(prev => [...prev, tagId]);
                }
              }}
              placeholder={t('customers.list.selectTagsPlaceholder', { ns: 'pages' })}
              className="h-10"
              options={tags.filter(tag => !selectedTagIds.includes(tag.id)).map(tag => ({ value: tag.id, label: tag.name }))}
            />
          </div>
          {/* 清除篩選：只在有篩選條件時出現（§3-5） */}
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="self-start md:self-auto text-sm font-medium text-fg-secondary hover:text-fg transition-colors"
            >
              {t('customers.list.clearFilters', { ns: 'pages' })}
            </button>
          )}
        </div>

        {selectedTagIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {selectedTagIds.map(tagId => {
              const tag = tags.find(item => item.id === tagId);
              if (!tag) return null;
              return (
                <span key={tagId} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-[13px] border border-line text-fg">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => setSelectedTagIds(prev => prev.filter(id => id !== tagId))}
                    className="px-1 text-fg-secondary hover:text-fg transition-colors"
                    aria-label={t('common.actions.remove')}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {error ? (
        <div className="p-8 text-center text-red-500">{t('common.messages.loadError')}</div>
      ) : listLoading ? (
        <TableSkeleton rows={10} columns={7} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '960px' }}>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader
                    userRole={userRole}
                    allSelected={bulkActions.allSelected}
                    onToggleAll={bulkActions.toggleSelectAll}
                    hasItems={listItems.length > 0}
                  />
                  <TableHead className="w-36">{t('common.table.columns.name')}</TableHead>
                  <TableHead className="w-48">{t('common.table.columns.email')}</TableHead>
                  <TableHead className="w-36">{t('common.table.columns.phone')}</TableHead>
                  <TableHead className="w-40">{t('common.table.columns.company')}</TableHead>
                  <TableHead className="w-28">{t('common.table.columns.createdBy')}</TableHead>
                  <TableHead className="w-36">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listItems.length === 0 ? (
                  <ListEmptyRow
                    colSpan={7}
                    filtered={hasFilters}
                    title={isArchived ? t('customers.archived.noCustomers', { ns: 'pages' }) : t('customers.list.noCustomers', { ns: 'pages' })}
                    hint={isArchived ? undefined : t('customers.list.emptyHint', { ns: 'pages' })}
                    action={!isArchived && canCreateCustomer && addButton}
                  />
                ) : (
                  listItems.map((customer) => (
                    <TableRow key={customer.id} className={pendingRowClass(customer)}>
                      <BulkSelectCell
                        userRole={userRole}
                        isSelected={bulkActions.isItemSelected(customer.id)}
                        onToggle={() => bulkActions.toggleItemSelection(customer.id)}
                        canDelete={canAdminDelete(customer, userRole)}
                      />
                      <TableCell className="font-medium truncate">
                        {isArchived ? customer.name : (
                          <Link to={`/customers/${customer.id}`} className="hover:text-accent transition-colors">{customer.name}</Link>
                        )}
                      </TableCell>
                      <TableCell className="truncate">{customer.email || <span className="text-fg-muted">—</span>}</TableCell>
                      <TableCell className="truncate">{customer.phone || <span className="text-fg-muted">—</span>}</TableCell>
                      <TableCell className="truncate">{customer.company || <span className="text-fg-muted">—</span>}</TableCell>
                      <TableCell className="truncate">{customer.createdByName || <span className="text-fg-muted">—</span>}</TableCell>
                      <TableCell>
                        {/* 操作欄：中性色、刪除用紅（R5）；「查看」改由點客戶名稱進入詳情 */}
                        <RowActions>
                          {isArchived ? (
                            isAdmin(userRole) && (
                              <RowAction onClick={() => moveCustomers([customer.id], false)}>
                                {t('customers.archived.restore', { ns: 'pages' })}
                              </RowAction>
                            )
                          ) : (
                            <>
                              {canModify(customer, userRole, currentUserId) && (
                                <RowAction onClick={() => setDialogCustomer(customer)}>{t('common.actions.edit')}</RowAction>
                              )}
                              {canModify(customer, userRole, currentUserId) && (
                                <RowAction tone="danger" onClick={() => handleDelete(customer.id)}>{t('common.actions.delete')}</RowAction>
                              )}
                            </>
                          )}
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <CustomerDialog
        isOpen={dialogCustomer !== undefined}
        onClose={() => setDialogCustomer(undefined)}
        customer={dialogCustomer}
      />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('customers.list.confirmDeleteTitle', { ns: 'pages' })}
        message={t('customers.list.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        t={t}
      />

      <ConfirmModal
        isOpen={bulkActions.showBulkDeleteModal}
        onClose={bulkActions.closeBulkDeleteModal}
        onConfirm={bulkActions.confirmBulkDelete}
        title={t('common.bulk.confirmDeleteTitle', { count: bulkActions.selectedItems.length, entity: t('customers.item', { ns: 'pages' }) })}
        message={t('common.bulk.confirmDeleteMessage', { count: bulkActions.selectedItems.length, entity: t('customers.item', { ns: 'pages' }) })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={bulkActions.isBulkDeleting}
        t={t}
      />
    </div>
  );
};

export default CustomerManagePage;
